/**
 * ExpiryDeadlineAuditDetails — structured renderer for a well-formed
 * `reservation.expiry_deadline_updated` audit event (CU-86cb7qgc9).
 *
 * Replaces the raw-JSON `AuditEntryDetails` dump for this one action only;
 * the caller (`unit-reservations-tab.tsx`) falls back to the raw-JSON
 * renderer for any malformed or non-matching event. Props-only, no data
 * fetching, no i18next import — labels are translated by the caller.
 */
import type { ExpiryDeadlineAuditDetails as ParsedExpiryDeadlineAuditDetails } from '../lib/expiry-deadline-audit';
import { formatDisplayDateTime } from '@ooh/ui/lib/date-format';

export interface ExpiryDeadlineAuditDetailsLabels {
  title: string;
  actorLabel: string;
  previousLabel: string;
  newLabel: string;
  reasonLabel: string;
  systemActorLabel: string;
}

export function ExpiryDeadlineAuditDetails({
  details,
  actorUserName,
  labels,
}: {
  details: ParsedExpiryDeadlineAuditDetails;
  actorUserName: string | null;
  labels: ExpiryDeadlineAuditDetailsLabels;
}) {
  return (
    <div className="mt-1 rounded-md border border-border/40 bg-muted/20 px-2.5 py-2 text-2xs space-y-0.5">
      <p className="font-medium text-foreground">{labels.title}</p>
      <p className="text-muted-foreground">
        <span className="font-medium text-foreground">{labels.actorLabel}</span>{' '}
        {actorUserName ?? labels.systemActorLabel}
      </p>
      <p className="text-muted-foreground">
        <span className="font-medium text-foreground">{labels.previousLabel}</span>{' '}
        {formatDisplayDateTime(details.previousExpiryDeadline)}
      </p>
      <p className="text-muted-foreground">
        <span className="font-medium text-foreground">{labels.newLabel}</span>{' '}
        {formatDisplayDateTime(details.newExpiryDeadline)}
      </p>
      <p className="text-muted-foreground">
        <span className="font-medium text-foreground">{labels.reasonLabel}</span> {details.reason}
      </p>
    </div>
  );
}
